import { DragIndicator } from '@mui/icons-material';
import {
    alpha,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableRowProps,
    styled,
} from '@mui/material';
import {
    closestCenter,
    CollisionDetection,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ReactNode, useMemo } from 'react';

export interface SortableRankingTableColumn {
    key: string;
    label: ReactNode;
    align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
    width?: string | number;
    padding?: 'normal' | 'checkbox' | 'none';
}

interface SortableRowProps {
    id: string;
    children: ReactNode;
    highlighted?: boolean;
    rowProps?: TableRowProps;
}

interface SortableRankingTableProps<T> {
    items: T[];
    columns: SortableRankingTableColumn[];
    getItemId: (item: T, index: number) => string;
    getItemKey?: (item: T, index: number) => string;
    renderRow: (item: T, index: number) => ReactNode;
    onReorder: (oldIndex: number, newIndex: number) => void;
    emptyState?: ReactNode;
    preBodyRows?: ReactNode;
    collisionDetection?: CollisionDetection;
    autoScroll?: boolean;
    tableSize?: 'small' | 'medium';
    getRowHighlighted?: (item: T, index: number) => boolean;
    getRowProps?: (item: T, index: number) => TableRowProps | undefined;
    dragHandleHeader?: ReactNode;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const DragHandleHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
}));

const SortableRow = ({
    id,
    children,
    highlighted = false,
    rowProps,
}: SortableRowProps): JSX.Element => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 999 : 'auto',
    };

    return (
        <StyledTableRow
            ref={setNodeRef}
            style={style}
            sx={highlighted ? { backgroundColor: (theme) => alpha(theme.palette.error.main, 0.2) } : undefined}
            {...rowProps}
        >
            <TableCell
                padding="none"
                width="40px"
                {...attributes}
                {...listeners}
                sx={{
                    cursor: 'grab',
                    color: isDragging ? 'primary.main' : 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                    display: 'table-cell',
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingLeft: '16px',
                    height: '100%',
                }}
            >
                <DragIndicator />
            </TableCell>
            {children}
        </StyledTableRow>
    );
};

export const SortableRankingTable = <T, >({
    items,
    columns,
    getItemId,
    getItemKey,
    renderRow,
    onReorder,
    emptyState,
    preBodyRows,
    collisionDetection = closestCenter,
    autoScroll,
    tableSize = 'medium',
    getRowHighlighted,
    getRowProps,
    dragHandleHeader,
}: SortableRankingTableProps<T>): JSX.Element => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor),
    );

    const itemIds = useMemo(
        () => items.map((item, index) => getItemId(item, index)),
        [getItemId, items],
    );

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = itemIds.indexOf(String(active.id));
        const newIndex = itemIds.indexOf(String(over.id));

        if (oldIndex !== -1 && newIndex !== -1) {
            onReorder(oldIndex, newIndex);
        }
    };

    return (
        <TableContainer component={Paper}>
            <DndContext
                autoScroll={autoScroll}
                collisionDetection={collisionDetection}
                sensors={sensors}
                onDragEnd={handleDragEnd}
            >
                <Table size={tableSize}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="none" width="40px">
                                <DragHandleHeader>
                                    {dragHandleHeader ?? <DragIndicator color="disabled" />}
                                </DragHandleHeader>
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell
                                    align={column.align}
                                    key={column.key}
                                    padding={column.padding}
                                    width={column.width}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.length === 0 && emptyState && (
                            <StyledTableRow>
                                <TableCell colSpan={columns.length + 1}>
                                    {emptyState}
                                </TableCell>
                            </StyledTableRow>
                        )}
                        {preBodyRows}
                        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                            {items.map((item, index) => (
                                <SortableRow
                                    highlighted={getRowHighlighted?.(item, index)}
                                    id={itemIds[index]}
                                    key={getItemKey?.(item, index) ?? itemIds[index]}
                                    rowProps={getRowProps?.(item, index)}
                                >
                                    {renderRow(item, index)}
                                </SortableRow>
                            ))}
                        </SortableContext>
                    </TableBody>
                </Table>
            </DndContext>
        </TableContainer>
    );
};
